<script setup lang="ts">
import { Button, Switch, ZhStatusPill } from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, refusalCode, requestFailure, type TaskDetail } from '@/api'
import { refusalSentence } from '@/lib/catalogListing'

/**
 * Whether one of this company's own tasks is in the catalog, and the switch that changes it. On publishes the task's
 * current version (listed on its role when this company has that role in the catalog); off withdraws it and records
 * it as left out, so its role's page stops offering it. Both are the task's own version save with `visibility`, the
 * one way a task reaches the catalog. While public, every version saved after reaches the catalog without it; the row
 * says which version the catalog holds and, when the catalog refused a newer one, offers to send it again.
 */
const props = defineProps<{ task: TaskDetail; disabled?: boolean }>()
const emit = defineEmits<{ changed: [] }>()

const busy = ref(false)
const error = ref('')
const planRefused = ref(false)

const publication = computed(() => props.task.catalogPublication ?? null)
const isPublic = computed(() => publication.value !== null)
const version = computed(() => props.task.currentVersion?.version ?? null)
const outdated = computed(
    () =>
        publication.value?.sourceVersion != null &&
        version.value !== null &&
        publication.value.sourceVersion < version.value,
)
const where = computed(() => {
    const role = publication.value?.assigneeRole
    return role ? `listed on the ${role} role` : 'on no role'
})

watch(
    () => props.task.id,
    () => {
        error.value = ''
        planRefused.value = false
    },
)

async function publish(visibility: 'public' | 'private'): Promise<void> {
    const base = props.task.currentVersionId
    if (!base || busy.value) return
    if (
        visibility === 'public' &&
        !isPublic.value &&
        !confirm(
            `Publish ${props.task.name || props.task.slug} to the catalog? Any company can then find and clone it.`,
        )
    ) {
        return
    }
    busy.value = true
    error.value = ''
    planRefused.value = false
    try {
        await api.saveTaskVersion(props.task.id, { baseVersionId: base, visibility })
        emit('changed')
    } catch (caught) {
        if (requestFailure(caught)?.status === 402 || refusalCode(caught) === 'catalog_publish_not_in_plan') {
            planRefused.value = true
            error.value = 'Your plan does not include catalog publishing.'
        } else {
            error.value = refusalSentence(caught, 'The catalog refused this change.')
        }
    } finally {
        busy.value = false
    }
}
</script>

<template>
    <div class="space-y-1 text-sm" data-testid="task-catalog-row">
        <div class="flex flex-wrap items-center gap-3">
            <span id="task-catalog-label" class="text-muted-foreground">Catalog</span>
            <Switch
                data-testid="task-catalog-toggle"
                aria-labelledby="task-catalog-label"
                :model-value="isPublic"
                :disabled="disabled || busy || !task.currentVersionId || task.status === 'deprecated'"
                @update:model-value="publish($event ? 'public' : 'private')"
            />
            <ZhStatusPill data-testid="task-catalog-state" :tone="isPublic ? 'running' : 'neutral'" :pulse="false">
                {{ isPublic ? 'public' : 'private' }}
            </ZhStatusPill>
            <span v-if="busy" class="text-muted-foreground" role="status">Saving…</span>
            <template v-else-if="publication">
                <span class="text-muted-foreground" data-testid="task-catalog-version">
                    The catalog holds v{{ publication.sourceVersion ?? version }}, {{ where }}.
                </span>
                <Button
                    v-if="outdated"
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="task-catalog-update"
                    :disabled="disabled"
                    @click="publish('public')"
                >Update catalog to v{{ version }}</Button>
            </template>
            <span v-else class="text-muted-foreground">Only this company can see it.</span>
        </div>
        <p v-if="error" class="text-destructive" role="alert" data-testid="task-catalog-error">
            {{ error }}
            <RouterLink v-if="planRefused" class="underline" to="/billing#plans">See plans</RouterLink>
        </p>
    </div>
</template>
